import { CSSProperties } from 'react';

import { columnIndexOf } from './sheet-grid.util';

/**
 * The styling an Excel Table puts on cells that carry none of their own.
 *
 * A worksheet drawn from cell formatting alone loses these entirely, and they are not a detail:
 * "Format as Table" is how most people colour a spreadsheet, and the banded header is what shows
 * a reader where the table is. The colours live nowhere in the cells — the file stores a range,
 * a style name and a theme, and Excel resolves the rest at paint time, which is what this does.
 *
 * An approximation, deliberately: Excel ships some sixty built-in table styles, and this reads
 * the two things that carry the look — which accent the name points at, and how strong the family
 * paints it. A cell's own fill always wins over anything here.
 */

/** The accents of the Office theme, which every built-in table style is drawn from. */
const ACCENTS = ['#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47'];

const NEUTRAL = '#7F7F7F';

export enum TableStyleFamily {
  DARK = 'dark',
  LIGHT = 'light',
  MEDIUM = 'medium',
}

const FAMILY_BY_PREFIX: Record<string, TableStyleFamily> = {
  tablestyledark: TableStyleFamily.DARK,
  tablestylelight: TableStyleFamily.LIGHT,
  tablestylemedium: TableStyleFamily.MEDIUM,
};

const THEME = /^(TableStyle(?:Light|Medium|Dark))(\d+)$/;

/** How far a band is washed out towards white, per family. */
const STRIPE_TINT: Record<TableStyleFamily, number> = {
  [TableStyleFamily.DARK]: 0.7,
  [TableStyleFamily.LIGHT]: 0.88,
  [TableStyleFamily.MEDIUM]: 0.8,
};

export type TableRegion = {
  accent: string;
  family: TableStyleFamily;
  from: { column: number; row: number };
  hasHeader: boolean;
  stripes: boolean;
  to: { column: number; row: number };
};

/** The shape of an ExcelJS table this reader uses — the model, by the fields it touches. */
export type ExcelTableModel = {
  headerRow?: boolean;
  ref?: string;
  style?: { showRowStripes?: boolean; theme?: null | string };
  totalsRow?: boolean;
};

const RANGE = /^([A-Za-z]+)(\d+):([A-Za-z]+)(\d+)$/;

const mixWithWhite = (hex: string, amount: number): string => {
  const channel = (at: number): number => {
    const value = parseInt(hex.slice(at, at + 2), 16);

    return Math.round(value + (255 - value) * amount);
  };

  return `rgb(${channel(1)}, ${channel(3)}, ${channel(5)})`;
};

/**
 * Which accent a style name points at.
 *
 * The built-in names run through the six theme accents in order from the second of each family,
 * the first being the neutral one — `TableStyleMedium2` is accent 1, which is the blue of the
 * default table, and `TableStyleMedium8` comes round to it again.
 */
const accentOf = (index: number): string =>
  index <= 1 ? NEUTRAL : ACCENTS[(index - 2) % ACCENTS.length];

export const tableRegionOf = (table: ExcelTableModel): null | TableRegion => {
  const bounds = table.ref ? RANGE.exec(table.ref.trim()) : null;

  if (!bounds) return null;

  const [, fromLetters, fromRow, toLetters, toRow] = bounds;
  const theme = THEME.exec(table.style?.theme ?? 'TableStyleMedium2');
  const family = theme ? FAMILY_BY_PREFIX[theme[1].toLowerCase()] : TableStyleFamily.MEDIUM;

  return {
    accent: accentOf(theme ? Number(theme[2]) : 2),
    family: family ?? TableStyleFamily.MEDIUM,
    from: { column: columnIndexOf(fromLetters), row: Number(fromRow) },
    hasHeader: table.headerRow !== false,
    stripes: table.style?.showRowStripes === true,
    to: { column: columnIndexOf(toLetters), row: Number(toRow) },
  };
};

export const tableRegionsOf = (tables: ExcelTableModel[]): TableRegion[] =>
  tables.map(tableRegionOf).filter((region): region is TableRegion => region !== null);

const headerStyle = (region: TableRegion): CSSProperties =>
  region.family === TableStyleFamily.LIGHT
    ? { borderBottom: `2px solid ${region.accent}`, fontWeight: 600 }
    : { backgroundColor: region.accent, color: '#FFFFFF', fontWeight: 600 };

/**
 * What a table paints on one cell, or nothing where no table reaches it.
 *
 * Bands count from the first row of data, so the row under the header is the first banded one —
 * which is where Excel starts them, and is what makes a one-row table look like a table.
 */
export const tableStyleAt = (
  regions: TableRegion[],
  row: number,
  column: number,
): CSSProperties | undefined => {
  for (const region of regions) {
    const inside =
      row >= region.from.row &&
      row <= region.to.row &&
      column >= region.from.column &&
      column <= region.to.column;

    if (!inside) continue;

    if (region.hasHeader && row === region.from.row) return headerStyle(region);

    if (!region.stripes) return undefined;

    const dataRow = row - region.from.row - (region.hasHeader ? 1 : 0);

    return dataRow % 2 === 0
      ? { backgroundColor: mixWithWhite(region.accent, STRIPE_TINT[region.family]) }
      : undefined;
  }

  return undefined;
};
